import React, { useState, useEffect } from 'react';

// Version: 2.1.0 - Fixed delete buttons with detailed logging
const LearningClockAdmin = ({ adminToken, isDarkMode, onBack }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  console.log('🔧 LearningClockAdmin v2.2.0 loaded - Delete modal + visibility controls');
  
  const [activeTab, setActiveTab] = useState('learn');
  const [times, setTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTime, setEditingTime] = useState(null);
  const [formData, setFormData] = useState({
    hour: '',
    minute: '',
    english_label: '',
    darija_label: '',
    darija_arabic: '',
    audio_url: '',
    am_pm: 'AM',
    is_visible: true,
    is_pro: false,
    available_modes: [],
    order: 0,
    // Quiz-specific fields
    question_text: '',
    options: ['', '', '', ''],
    options_arabic: ['', '', '', ''],
    correct_answer_index: 0
  });
  const [message, setMessage] = useState(null);
  
  // Help content state
  const [helpContent, setHelpContent] = useState(null);
  const [helpLoading, setHelpLoading] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [timeToDelete, setTimeToDelete] = useState(null);
  
  // Game settings state
  const [gameSettings, setGameSettings] = useState({
    is_active: true,
    is_premium: false
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Audio and tab visibility settings
  const [modeSettings, setModeSettings] = useState({
    learn_audio_enabled: true,
    test1_audio_enabled: true,
    test2_tab_visible: true
  });

  useEffect(() => {
    if (activeTab === 'help') {
      fetchHelpContent();
    } else {
      fetchTimes(activeTab);
    }
    fetchGameSettings();
  }, [activeTab]);

  const fetchGameSettings = async () => {
    try {
      const response = await fetch(`${API}/games`);
      if (response.ok) {
        const data = await response.json();
        const learningClockGame = data.games?.find(g => g.id === 'learning-clock');
        if (learningClockGame) {
          setGameSettings({
            is_active: learningClockGame.is_active !== false,
            is_premium: learningClockGame.is_premium || false
          });
          // Load mode-specific settings
          setModeSettings({
            learn_audio_enabled: learningClockGame.learn_audio_enabled !== false,
            test1_audio_enabled: learningClockGame.test1_audio_enabled !== false,
            test2_tab_visible: learningClockGame.test2_tab_visible !== false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching game settings:', error);
    }
  };

  const updateGameSettings = async (field, value) => {
    try {
      setSettingsLoading(true);
      const response = await fetch(`${API}/admin/games/learning-clock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          [field]: value
        })
      });

      if (response.ok) {
        setGameSettings(prev => ({ ...prev, [field]: value }));
        const statusText = field === 'is_active' 
          ? (value ? 'visible to users' : 'hidden from users')
          : (value ? 'set to PRO only' : 'set to FREE');
        showMessage(`✅ Learning Clock ${statusText}!`, 'success');
      } else {
        showMessage('Failed to update settings', 'error');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      showMessage('Error updating settings', 'error');
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateModeSettings = async (field, value) => {
    try {
      setSettingsLoading(true);
      const response = await fetch(`${API}/admin/games/learning-clock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          [field]: value
        })
      });

      if (response.ok) {
        setModeSettings(prev => ({ ...prev, [field]: value }));
        let statusText = '';
        if (field === 'learn_audio_enabled') {
          statusText = value ? 'Audio enabled for Learn mode' : 'Audio disabled for Learn mode';
        } else if (field === 'test1_audio_enabled') {
          statusText = value ? 'Audio enabled for Read & Choose' : 'Audio disabled for Read & Choose';
        } else if (field === 'test2_tab_visible') {
          statusText = value ? 'Listen & Pick tab is now visible' : 'Listen & Pick tab is now hidden';
        }
        showMessage(`✅ ${statusText}!`, 'success');
      } else {
        showMessage('Failed to update settings', 'error');
      }
    } catch (error) {
      console.error('Error updating mode settings:', error);
      showMessage('Error updating settings', 'error');
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchTimes = async (mode) => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/learning-clock/times`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter times by mode
        const filteredTimes = (data.times || []).filter(time => 
          time.available_modes && time.available_modes.includes(mode)
        );
        setTimes(filteredTimes);
      } else {
        showMessage(`Failed to load times: ${response.status}`, 'error');
      }
    } catch (error) {
      console.error('Error fetching times:', error);
      showMessage('Failed to load times', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchHelpContent = async () => {
    try {
      setHelpLoading(true);
      const response = await fetch(`${API}/learning-clock/help`);
      
      if (response.ok) {
        const data = await response.json();
        setHelpContent(data);
      }
    } catch (error) {
      console.error('Error fetching help:', error);
    } finally {
      setHelpLoading(false);
    }
  };

  const saveHelpContent = async () => {
    try {
      setHelpLoading(true);
      const response = await fetch(`${API}/admin/learning-clock/help`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(helpContent)
      });
      
      if (response.ok) {
        showMessage('Help content updated successfully!', 'success');
      } else {
        showMessage('Failed to update help content', 'error');
      }
    } catch (error) {
      console.error('Error saving help:', error);
      showMessage('Error saving help content', 'error');
    } finally {
      setHelpLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Ensure the current mode is in available_modes
      const modesSet = new Set(formData.available_modes);
      modesSet.add(activeTab);
      const updatedFormData = {
        ...formData,
        available_modes: Array.from(modesSet)
      };
      
      const url = editingTime
        ? `${API}/admin/learning-clock/times/${editingTime.id}`
        : `${API}/admin/learning-clock/times`;
      
      const method = editingTime ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(updatedFormData)
      });
      
      if (response.ok) {
        showMessage(editingTime ? 'Time updated successfully!' : 'Time created successfully!', 'success');
        setShowForm(false);
        setEditingTime(null);
        resetForm();
        fetchTimes(activeTab);
      } else {
        showMessage('Failed to save time', 'error');
      }
    } catch (error) {
      console.error('Error saving time:', error);
      showMessage('Error saving time', 'error');
    }
  };

  const handleEdit = (time) => {
    setEditingTime(time);
    setFormData({
      hour: time.hour,
      minute: time.minute,
      english_label: time.english_label,
      darija_label: time.darija_label,
      darija_arabic: time.darija_arabic || '',
      audio_url: time.audio_url || '',
      am_pm: time.am_pm,
      is_visible: time.is_visible !== undefined ? time.is_visible : true,
      is_pro: time.is_pro || false,
      available_modes: time.available_modes || [activeTab],
      order: time.order,
      question_text: time.question_text || '',
      options: time.options || ['', '', '', ''],
      options_arabic: time.options_arabic || ['', '', '', ''],
      correct_answer_index: time.correct_answer_index || 0
    });
    setShowForm(true);
  };

  const confirmDelete = (time) => {
    setTimeToDelete(time);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!timeToDelete) return;
    
    try {
      const response = await fetch(`${API}/admin/learning-clock/times/${timeToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        showMessage('Time deleted successfully!', 'success');
        setShowDeleteModal(false);
        setTimeToDelete(null);
        fetchTimes(activeTab);
      } else {
        showMessage(`Failed to delete time: ${response.status}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting time:', error);
      showMessage('Error deleting time: ' + error.message, 'error');
    }
  };

  const moveTime = async (timeId, direction) => {
    const idx = times.findIndex(t => t.id === timeId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= times.length) return;
    const reordered = [...times];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    try {
      const response = await fetch(`${API}/admin/learning-clock/times/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ time_ids: reordered.map(t => t.id) })
      });
      if (response.ok) fetchTimes(activeTab);
      else showMessage('Error reordering', 'error');
    } catch (e) { showMessage('Error reordering', 'error'); }
  };

  const resetForm = () => {
    setFormData({
      hour: '',
      minute: '',
      english_label: '',
      darija_label: '',
      darija_arabic: '',
      audio_url: '',
      am_pm: 'AM',
      is_visible: true,
      is_pro: false,
      available_modes: [activeTab],
      order: 0,
      question_text: '',
      options: ['', '', '', ''],
      options_arabic: ['', '', '', ''],
      correct_answer_index: 0
    });
    setEditingTime(null);
    setShowForm(false);
  };

  const getModeConfig = (mode) => {
    const configs = {
      'learn': {
        title: 'Learn Time in Darija Admin',
        icon: '📚',
        color: 'blue',
        description: 'Manage times for learning mode - users see time with English, Darija, and Arabic labels'
      },
      'test1': {
        title: 'Read & Choose Admin',
        icon: '🎮',
        color: 'green',
        description: 'Manage quiz questions for Test Mode 1 - users see time and choose correct label from options'
      },
      'test2': {
        title: 'Listen & Pick Time Admin',
        icon: '🎧',
        color: 'purple',
        description: 'Manage quiz questions for Test Mode 2 - users hear audio and pick correct time from options'
      },
      'help': {
        title: 'How to Use Learning Clock Admin',
        icon: '❓',
        color: 'orange',
        description: 'Edit help content and instructions for users'
      }
    };
    return configs[mode];
  };

  const updateHelpField = (mode, field, value) => {
    setHelpContent(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        [field]: value
      }
    }));
  };

  const updateHelpItem = (mode, index, value) => {
    setHelpContent(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        items: prev[mode].items.map((item, i) => i === index ? value : item)
      }
    }));
  };

  const addHelpItem = (mode) => {
    setHelpContent(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        items: [...prev[mode].items, '']
      }
    }));
  };

  const removeHelpItem = (mode, index) => {
    setHelpContent(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        items: prev[mode].items.filter((_, i) => i !== index)
      }
    }));
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} p-4`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className={`px-4 py-2 rounded-lg font-semibold transition-all mb-4 ${
              isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'
            }`}
          >
            ← Back to Admin
          </button>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🕐 Learning Clock Admin
              </h1>
              <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Manage times and help content for all modes
              </p>
            </div>
            
            {/* Visibility & Access Controls */}
            <div className={`flex flex-wrap gap-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              <button
                onClick={() => updateGameSettings('is_active', !gameSettings.is_active)}
                disabled={settingsLoading}
                className={`px-6 py-3 rounded-lg font-bold transition-all shadow-md ${
                  gameSettings.is_active
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={gameSettings.is_active ? 'Click to hide from users' : 'Click to show to users'}
              >
                {gameSettings.is_active ? '👁️ VISIBLE' : '🚫 HIDDEN'}
              </button>
              
              <button
                onClick={() => updateGameSettings('is_premium', !gameSettings.is_premium)}
                disabled={settingsLoading}
                className={`px-6 py-3 rounded-lg font-bold transition-all shadow-md ${
                  gameSettings.is_premium
                    ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={gameSettings.is_premium ? 'Click to make FREE' : 'Click to make PRO only'}
              >
                {gameSettings.is_premium ? '🔒 PRO ONLY' : '🔓 FREE'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['learn', 'test1', 'test2', 'help'].map((tab) => {
            const config = getModeConfig(tab);
            return (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setShowForm(false);
                }}
                className={`px-6 py-3 rounded-lg font-bold transition-all ${
                  activeTab === tab
                    ? config.color === 'blue'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : config.color === 'green'
                      ? 'bg-green-600 text-white shadow-lg'
                      : config.color === 'purple'
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-orange-600 text-white shadow-lg'
                    : isDarkMode
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {config.icon} {config.title}
              </button>
            );
          })}
        </div>

        {/* Success/Error Message */}
        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' 
              ? isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
              : isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Tab Description */}
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {getModeConfig(activeTab).description}
            </p>
            
            {/* Audio Toggle for Learn and Test1, Tab Visibility for Test2 */}
            {activeTab === 'learn' && (
              <button
                onClick={() => updateModeSettings('learn_audio_enabled', !modeSettings.learn_audio_enabled)}
                disabled={settingsLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  modeSettings.learn_audio_enabled
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {modeSettings.learn_audio_enabled ? '🔊 Audio ON' : '🔇 Audio OFF'}
              </button>
            )}
            
            {activeTab === 'test1' && (
              <button
                onClick={() => updateModeSettings('test1_audio_enabled', !modeSettings.test1_audio_enabled)}
                disabled={settingsLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  modeSettings.test1_audio_enabled
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {modeSettings.test1_audio_enabled ? '🔊 Audio ON' : '🔇 Audio OFF'}
              </button>
            )}
            
            {activeTab === 'test2' && (
              <button
                onClick={() => updateModeSettings('test2_tab_visible', !modeSettings.test2_tab_visible)}
                disabled={settingsLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  modeSettings.test2_tab_visible
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {modeSettings.test2_tab_visible ? '👁️ Tab VISIBLE' : '🚫 Tab HIDDEN'}
              </button>
            )}
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'help' ? (
          // Help Content Editor
          <HelpContentEditor 
            helpContent={helpContent}
            helpLoading={helpLoading}
            isDarkMode={isDarkMode}
            updateHelpField={updateHelpField}
            updateHelpItem={updateHelpItem}
            addHelpItem={addHelpItem}
            removeHelpItem={removeHelpItem}
            saveHelpContent={saveHelpContent}
          />
        ) : (
          // Times CRUD
          <>
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className={`px-6 py-3 rounded-lg font-bold transition-all shadow-lg ${
                  getModeConfig(activeTab).color === 'blue'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : getModeConfig(activeTab).color === 'green'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                } text-white`}
              >
                + Add New Time
              </button>
            </div>

            {/* Form Modal */}
            {showForm && (
              <TimeFormModal
                formData={formData}
                setFormData={setFormData}
                editingTime={editingTime}
                isDarkMode={isDarkMode}
                handleSubmit={handleSubmit}
                resetForm={resetForm}
                activeMode={activeTab}
              />
            )}

            {/* Times List */}
            <TimesTable
              times={times}
              loading={loading}
              isDarkMode={isDarkMode}
              handleEdit={handleEdit}
              handleDelete={confirmDelete}
              handleMove={moveTime}
            />
          </>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && timeToDelete && (
        <DeleteConfirmModal
          time={timeToDelete}
          isDarkMode={isDarkMode}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setTimeToDelete(null);
          }}
        />
      )}
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ time, isDarkMode, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full p-6 rounded-2xl shadow-2xl ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <span className="text-4xl">🗑️</span>
          </div>
          
          {/* Title */}
          <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Delete Time Entry?
          </h3>
          
          {/* Description */}
          <div className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <p className="mb-3">Are you sure you want to delete this time?</p>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <p className="font-bold text-lg">
                {time.hour}:{time.minute.toString().padStart(2, '0')} {time.am_pm}
              </p>
              <p className="text-sm mt-1">{time.english_label}</p>
              <p className="text-sm text-purple-500">{time.darija_label}</p>
            </div>
            <p className="mt-3 text-sm text-red-500">
              ⚠️ This action cannot be undone.
            </p>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className={`flex-1 px-6 py-3 rounded-lg font-bold transition-all ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-all"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Help Content Editor Component
const HelpContentEditor = ({ 
  helpContent, 
  helpLoading, 
  isDarkMode, 
  updateHelpField,
  updateHelpItem,
  addHelpItem,
  removeHelpItem,
  saveHelpContent 
}) => {
  if (helpLoading || !helpContent) {
    return (
      <div className={`p-12 text-center rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="animate-spin inline-block w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full"></div>
        <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading help content...</p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="space-y-6">
        {/* Main Title */}
        <div>
          <label className={`block text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Main Title
          </label>
          <input
            type="text"
            value={helpContent.title || ''}
            onChange={(e) => updateHelpField('title', null, e.target.value)}
            className={`w-full px-4 py-2 rounded-lg border-2 ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-800'
            } focus:outline-none focus:border-orange-500`}
          />
        </div>

        {/* Learn Mode Section */}
        <HelpSection
          title="Learn Mode Section"
          color="blue"
          section={helpContent.learn_mode}
          sectionKey="learn_mode"
          isDarkMode={isDarkMode}
          updateHelpField={updateHelpField}
          updateHelpItem={updateHelpItem}
          addHelpItem={addHelpItem}
          removeHelpItem={removeHelpItem}
        />

        {/* Test1 Mode Section */}
        <HelpSection
          title="Test Mode 1 Section (Read & Choose)"
          color="green"
          section={helpContent.test1_mode}
          sectionKey="test1_mode"
          isDarkMode={isDarkMode}
          updateHelpField={updateHelpField}
          updateHelpItem={updateHelpItem}
          addHelpItem={addHelpItem}
          removeHelpItem={removeHelpItem}
        />

        {/* Test2 Mode Section */}
        <HelpSection
          title="Test Mode 2 Section (Listen & Pick)"
          color="purple"
          section={helpContent.test2_mode}
          sectionKey="test2_mode"
          isDarkMode={isDarkMode}
          updateHelpField={updateHelpField}
          updateHelpItem={updateHelpItem}
          addHelpItem={addHelpItem}
          removeHelpItem={removeHelpItem}
        />

        {/* Features Section */}
        <HelpSection
          title="Features Section"
          color="pink"
          section={helpContent.features}
          sectionKey="features"
          isDarkMode={isDarkMode}
          updateHelpField={updateHelpField}
          updateHelpItem={updateHelpItem}
          addHelpItem={addHelpItem}
          removeHelpItem={removeHelpItem}
        />

        {/* Save Button */}
        <div className="pt-4">
          <button
            onClick={saveHelpContent}
            className="w-full px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-lg font-bold transition-all"
          >
            💾 Save Help Content
          </button>
        </div>
      </div>
    </div>
  );
};

// Help Section Component
const HelpSection = ({ 
  title, 
  color, 
  section, 
  sectionKey, 
  isDarkMode, 
  updateHelpField,
  updateHelpItem,
  addHelpItem,
  removeHelpItem 
}) => {
  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
    green: 'border-green-500 bg-green-50 dark:bg-green-900/20',
    purple: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20',
    pink: 'border-pink-500 bg-pink-50 dark:bg-pink-900/20'
  };

  return (
    <div className={`p-4 rounded-lg border-2 ${colorClasses[color]} ${isDarkMode ? 'dark' : ''}`}>
      <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
        {title}
      </h3>
      
      {/* Section Title */}
      <div className="mb-3">
        <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Section Title
        </label>
        <input
          type="text"
          value={section?.title || ''}
          onChange={(e) => updateHelpField(sectionKey, 'title', e.target.value)}
          className={`w-full px-3 py-2 rounded border ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-800'
          } focus:outline-none focus:border-${color}-500`}
        />
      </div>

      {/* Items List */}
      <div className="space-y-2">
        <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Items
        </label>
        {section?.items?.map((item, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="text"
              value={item}
              onChange={(e) => updateHelpItem(sectionKey, index, e.target.value)}
              className={`flex-1 px-3 py-2 rounded border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-800'
              } focus:outline-none focus:border-${color}-500`}
            />
            <button
              onClick={() => removeHelpItem(sectionKey, index)}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-all"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          onClick={() => addHelpItem(sectionKey)}
          className={`w-full px-4 py-2 border-2 border-dashed rounded transition-all ${
            isDarkMode
              ? 'border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-300'
              : 'border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-700'
          }`}
        >
          + Add Item
        </button>
      </div>
    </div>
  );
};

// Time Form Modal Component
const TimeFormModal = ({ 
  formData, 
  setFormData, 
  editingTime, 
  isDarkMode, 
  handleSubmit, 
  resetForm,
  activeMode 
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="p-6">
          <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {editingTime ? 'Edit Time' : 'Add New Time'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Time Input */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Hour (1-12)
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  required
                  value={formData.hour}
                  onChange={(e) => setFormData({...formData, hour: parseInt(e.target.value)})}
                  className={`w-full px-4 py-2 rounded-lg border-2 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-800'
                  } focus:outline-none focus:border-blue-500`}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Minute (0-59)
                </label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  required
                  value={formData.minute}
                  onChange={(e) => setFormData({...formData, minute: parseInt(e.target.value)})}
                  className={`w-full px-4 py-2 rounded-lg border-2 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-800'
                  } focus:outline-none focus:border-blue-500`}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  AM/PM
                </label>
                <select
                  value={formData.am_pm}
                  onChange={(e) => setFormData({...formData, am_pm: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border-2 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-800'
                  } focus:outline-none focus:border-blue-500`}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>

            {/* English Label, Darija Label, Arabic Script - ONLY for Learning mode */}
            {activeMode === 'learn' && (
              <>
                {/* English Label */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    English Label
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.english_label}
                    onChange={(e) => setFormData({...formData, english_label: e.target.value})}
                    placeholder="e.g., One o'clock"
                    className={`w-full px-4 py-2 rounded-lg border-2 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-800'
                    } focus:outline-none focus:border-blue-500`}
                  />
                </div>

                {/* Darija Label (Latin) */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Darija Label (Latin Script)
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.darija_label}
                    onChange={(e) => setFormData({...formData, darija_label: e.target.value})}
                    placeholder="e.g., Wa7da l3chiya"
                    className={`w-full px-4 py-2 rounded-lg border-2 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-800'
                    } focus:outline-none focus:border-blue-500`}
                  />
                </div>

                {/* Arabic Script */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Arabic Script (العربية)
                  </label>
                  <input
                    type="text"
                    value={formData.darija_arabic}
                    onChange={(e) => setFormData({...formData, darija_arabic: e.target.value})}
                    placeholder="e.g., وحدة العشية"
                    dir="rtl"
                    className={`w-full px-4 py-2 rounded-lg border-2 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-800'
                    } focus:outline-none focus:border-blue-500`}
                  />
                </div>
              </>
            )}

            {/* Audio URL - Show for all modes but mark as required for test2 */}
            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Audio URL {activeMode === 'test2' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="url"
                value={formData.audio_url}
                onChange={(e) => setFormData({...formData, audio_url: e.target.value})}
                placeholder="https://www.dropbox.com/..."
                required={activeMode === 'test2'}
                className={`w-full px-4 py-2 rounded-lg border-2 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-800'
                } focus:outline-none focus:border-blue-500`}
              />
              {activeMode === 'test2' && (
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Required for Listen & Pick mode
                </p>
              )}
            </div>

            {/* Quiz-Specific Fields for test1 and test2 */}
            {(activeMode === 'test1' || activeMode === 'test2') && (
              <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-300'}`}>
                <h3 className={`font-bold mb-3 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                  🎯 Quiz Options (Multiple Choice)
                </h3>
                
                {/* Multiple Choice Options - Each option has Latin + Arabic Script */}
                <div className="space-y-3">
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Answer Options <span className="text-red-500">*</span>
                    <span className="text-xs ml-2 block mt-1">(Create 3-4 options with Latin + Arabic Script, mark one as correct)</span>
                  </label>
                  
                  {formData.options.map((option, index) => (
                    <div key={index} className={`p-3 rounded-lg border-2 ${
                      formData.correct_answer_index === index
                        ? isDarkMode
                          ? 'bg-green-900/30 border-green-600'
                          : 'bg-green-50 border-green-500'
                        : isDarkMode 
                          ? 'bg-gray-700 border-gray-600' 
                          : 'bg-white border-gray-300'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="radio"
                          name="correct_answer"
                          checked={formData.correct_answer_index === index}
                          onChange={() => setFormData({...formData, correct_answer_index: index})}
                          className="w-5 h-5 text-green-600"
                          title="Mark as correct answer"
                        />
                        <span className={`text-sm font-bold ${
                          formData.correct_answer_index === index
                            ? 'text-green-500'
                            : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Option {index + 1} {formData.correct_answer_index === index ? '✓ Correct' : ''}
                        </span>
                        {index > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newOptions = formData.options.filter((_, i) => i !== index);
                              const newOptionsArabic = (formData.options_arabic || []).filter((_, i) => i !== index);
                              setFormData({
                                ...formData, 
                                options: newOptions,
                                options_arabic: newOptionsArabic,
                                correct_answer_index: formData.correct_answer_index === index ? 0 : formData.correct_answer_index
                              });
                            }}
                            className="ml-auto px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-all"
                          >
                            ✕ Remove
                          </button>
                        )}
                      </div>
                      
                      {/* Latin Script Input */}
                      <div className="mb-2">
                        <label className={`block text-xs mb-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                          Latin Script (Darija)
                        </label>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...formData.options];
                            newOptions[index] = e.target.value;
                            setFormData({...formData, options: newOptions});
                          }}
                          placeholder="e.g., Wa7da l3chiya"
                          className={`w-full px-3 py-2 rounded border ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white' 
                              : 'bg-white border-gray-300 text-gray-800'
                          } focus:outline-none focus:border-purple-500`}
                        />
                      </div>
                      
                      {/* Arabic Script Input */}
                      <div>
                        <label className={`block text-xs mb-1 ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>
                          Arabic Script (العربية)
                        </label>
                        <input
                          type="text"
                          value={(formData.options_arabic || [])[index] || ''}
                          onChange={(e) => {
                            const newOptionsArabic = [...(formData.options_arabic || formData.options.map(() => ''))];
                            newOptionsArabic[index] = e.target.value;
                            setFormData({...formData, options_arabic: newOptionsArabic});
                          }}
                          placeholder="e.g., وحدة العشية"
                          dir="rtl"
                          className={`w-full px-3 py-2 rounded border ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white' 
                              : 'bg-white border-gray-300 text-gray-800'
                          } focus:outline-none focus:border-pink-500`}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {formData.options.length < 6 && (
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData, 
                        options: [...formData.options, ''],
                        options_arabic: [...(formData.options_arabic || formData.options.map(() => '')), '']
                      })}
                      className={`w-full px-4 py-2 border-2 border-dashed rounded transition-all ${
                        isDarkMode
                          ? 'border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-300'
                          : 'border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-700'
                      }`}
                    >
                      + Add Another Option
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Order */}
            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Order (for sorting)
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                className={`w-full px-4 py-2 rounded-lg border-2 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-800'
                } focus:outline-none focus:border-blue-500`}
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-bold transition-all"
              >
                {editingTime ? 'Update Time' : 'Create Time'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className={`px-6 py-3 rounded-lg font-bold transition-all ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Times Table Component
const TimesTable = ({ times, loading, isDarkMode, handleEdit, handleDelete, handleMove }) => {
  if (loading) {
    return (
      <div className={`rounded-xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-12 text-center">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading times...</p>
        </div>
      </div>
    );
  }

  if (times.length === 0) {
    return (
      <div className={`rounded-xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-12 text-center">
          <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No times found for this mode. Click "Add New Time" to create one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <tr>
              <th className={`px-3 py-3 text-center text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ width: '60px' }}>
                Order
              </th>
              <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Time
              </th>
              <th className={`px-4 py-3 text-right text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {times.map((time, index) => (
              <tr key={time.id} className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                <td className="px-3 py-4 whitespace-nowrap text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <button
                      onClick={() => handleMove(time.id, 'up')}
                      disabled={index === 0}
                      className={`p-0.5 rounded text-xs leading-none transition-all ${
                        index === 0
                          ? 'opacity-20 cursor-not-allowed'
                          : isDarkMode
                            ? 'hover:bg-gray-600 text-gray-400'
                            : 'hover:bg-gray-200 text-gray-500'
                      }`}
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleMove(time.id, 'down')}
                      disabled={index === times.length - 1}
                      className={`p-0.5 rounded text-xs leading-none transition-all ${
                        index === times.length - 1
                          ? 'opacity-20 cursor-not-allowed'
                          : isDarkMode
                            ? 'hover:bg-gray-600 text-gray-400'
                            : 'hover:bg-gray-200 text-gray-500'
                      }`}
                    >
                      ▼
                    </button>
                  </div>
                </td>
                <td className={`px-4 py-4 whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <span className="font-bold text-lg">{time.hour}:{time.minute.toString().padStart(2, '0')} {time.am_pm}</span>
                </td>
                <td className={`px-4 py-4 whitespace-nowrap text-right text-sm font-medium`}>
                  <button
                    onClick={() => handleEdit(time)}
                    className="text-blue-600 hover:text-blue-900 mr-4 px-3 py-1 rounded hover:bg-blue-100 transition-all"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDelete(time)}
                    className="text-red-600 hover:text-red-900 px-3 py-1 rounded hover:bg-red-100 transition-all"
                  >
                    🗑️ Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LearningClockAdmin;
